// src/pages/league-dashboard/free-agent-search/SearchControls.js - COMPLETE WITH ADVANCED FILTERS

import React, { useState, useEffect } from 'react';
import {
  Search, ChevronDown, UserPlus, AlertTriangle,
  Shield, CheckSquare, Square, Filter, Calendar, TrendingUp,
  X, ChevronUp, Circle
} from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { useBatchSelection } from './BatchSelectionProvider';
import { canAddPlayers } from '../../../utils/RosterCapacityUtils';
import { validateCustomWindow } from '../../../utils/customWindowValidator';

const SearchControls = ({ state, leagueId, isCommissionerMode, activeTeamName }) => {
  const {
    selectionStats,
    getSelectedPlayerObjects,
    clearAllSelections,
    bulkMode,
  } = useBatchSelection();
  
  const {
    // Filters & Search
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    positionFilter,
    setPositionFilter,
    viewMode,
    setViewMode,
    showAll,
    setShowAll,
    totalCount,
    
    // Functions
    loadPlayers,
    handleBatchAddPlayers,
    
    // State
    players,
    loading,
    rosterCapacityAnalysis,
    transactionsEnabled,

    // Recent toggle (rolling-row visibility)
    showRecent,
    setShowRecent,

    // Error handling
    setError
  } = state;

  // ========================================
  // ADVANCED FILTERS STATE - FROM PARENT
  // ========================================
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Use filters from parent state
  const advancedFilters = state.advancedFilters;
  const setAdvancedFilters = state.setAdvancedFilters;

  // 9o.6.e Commit 8: MLB qualification thresholds (PA ≥ 502, IP ≥ 162) only
  // apply to season stats. When the user picks a rolling window, the toggle
  // is greyed out + force-evaluated to false in the UI. Backend already filters
  // against season stats only, so a false-when-disabled value is the correct wire.
  const qualificationDisabled = (advancedFilters?.dateRange ?? 'season') !== 'season';
  const qualificationDisabledTooltip =
    'Qualification thresholds (PA ≥ 502, IP ≥ 162) apply to season stats only. ' +
    'Switch to Season window to enable.';

  // 9o.6.e Commit 8: clear qualification flags when the user switches off season.
  // Per discipline 14: state must match displayed UI. Otherwise the downstream
  // applyFrontendFilters in usePlayerData keeps filtering by the stale flag.
  useEffect(() => {
    if (
      qualificationDisabled &&
      setAdvancedFilters &&
      (advancedFilters?.hitterQualified || advancedFilters?.pitcherQualified)
    ) {
      setAdvancedFilters(prev => ({
        ...prev,
        hitterQualified: false,
        pitcherQualified: false,
      }));
    }
  }, [qualificationDisabled, advancedFilters?.hitterQualified, advancedFilters?.pitcherQualified, setAdvancedFilters]);

  // Position options
  const hitterPositions = [
    { value: 'all', label: 'All Positions' },
    { value: 'C', label: 'Catcher (C)' },
    { value: '1B', label: 'First Base (1B)' },
    { value: '2B', label: 'Second Base (2B)' },
    { value: '3B', label: 'Third Base (3B)' },
    { value: 'SS', label: 'Shortstop (SS)' },
    { value: 'MI', label: 'Middle Infield (2B/SS)' },
    { value: 'CI', label: 'Corner Infield (1B/3B)' },
    { value: 'OF', label: 'Outfield (OF)' },
    { value: 'DH', label: 'Designated Hitter (DH)' },
    { value: 'UT', label: 'Utility' }
  ];

  const pitcherPositions = [
    { value: 'all', label: 'All Pitchers' },
    { value: 'SP', label: 'Starting Pitcher (SP)' },
    { value: 'RP', label: 'Relief Pitcher (RP)' },
    { value: 'CL', label: 'Closer (CL)' }
  ];

  // ========================================
  // FILTER APPLICATION
  // ========================================
  const applyAdvancedFilters = () => {
    // Trigger a reload which will automatically use the current advancedFilters from state
    if (typeof loadPlayers === 'function') {
      loadPlayers(0, false);
    }
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      minAB: '',
      maxAB: '',
      minHR: '',
      maxHR: '',
      minG: '',
      maxG: '',
      minPrice: '',
      maxPrice: '',
      hitterQualified: false,
      minK: '',
      maxK: '',
      minGS: '',
      maxGS: '',
      minIP: '',
      maxIP: '',
      pitcherQualified: false,
      dateRange: 'season',
      customStartDate: '',
      customEndDate: '',
      qualifiedPA: 502,
      qualifiedIP: 162
    });
  };

  // ========================================
  // ROSTER CAPACITY VALIDATION
  // ========================================
  const validateBatchAddition = () => {
    if (!rosterCapacityAnalysis) {
      return {
        canAdd: false,
        message: 'Checking roster capacity...',
        type: 'loading'
      };
    }

    if (selectionStats.count === 0) {
      return {
        canAdd: false,
        message: 'No players selected',
        type: 'info'
      };
    }

    const capacityCheck = canAddPlayers(selectionStats.count, rosterCapacityAnalysis);
    
    if (!capacityCheck.canAdd) {
      return {
        canAdd: false,
        message: `Cannot add ${selectionStats.count} players: ${capacityCheck.message}`,
        type: 'error'
      };
    }

    return {
      canAdd: true,
      message: `Ready to assign positions for ${selectionStats.count} player${selectionStats.count !== 1 ? 's' : ''}`,
      type: 'success'
    };
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    setShowAll(newMode === 'all_players');
    setSearchTerm('');
    setPositionFilter('all');
    clearAllSelections();
    clearAdvancedFilters();
    // usePlayerData's useEffect re-fires when showAll changes — no explicit loadPlayers needed
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchTerm('');
    setPositionFilter('all');
    clearAllSelections();
    clearAdvancedFilters();
    // usePlayerData's useEffect re-fires when activeTab changes — no explicit loadPlayers needed
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    clearAllSelections();
    // Call loadPlayers which will use current searchTerm, activeTab, etc.
    if (typeof loadPlayers === 'function') {
      loadPlayers(0, false);
    }
  };

  const handleIntelligentBatchAdd = () => {
    const validation = validateBatchAddition();
    
    if (!validation.canAdd) {
      setError(validation.message);
      setTimeout(() => setError(''), 5000);
      return;
    }

    const selectedPlayerObjects = getSelectedPlayerObjects(players);
    handleBatchAddPlayers(selectedPlayerObjects);
  };

  const renderCapacityIndicator = () => {
    if (!rosterCapacityAnalysis) return null;

    const { availableSlots, totalSlots, usedSlots } = rosterCapacityAnalysis;
    const percentFull = (usedSlots / totalSlots) * 100;
    const dotColor = percentFull < 70 ? '#4ade80' : percentFull < 90 ? '#eab308' : '#f87171';

    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <Circle className="w-2 h-2 flex-shrink-0" style={{ color: dotColor, fill: dotColor }} />
        <span>{usedSlots}/{totalSlots} roster</span>
        {availableSlots > 0 ? (
          <span style={{ color: dotColor }}>({availableSlots} open)</span>
        ) : (
          <span className="text-red-400">(full)</span>
        )}
      </div>
    );
  };

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <>
      {/* BATCH ADD BANNER */}
      {bulkMode && selectionStats.hasSelections && (
        <div className={`${dynastyTheme.components.card.base} p-4 mb-4 border-l-4 ${
          rosterCapacityAnalysis?.availableSlots >= selectionStats.count
            ? 'border-yellow-400'
            : 'border-red-400 bg-red-500/10'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserPlus className={`w-5 h-5 ${rosterCapacityAnalysis?.availableSlots >= selectionStats.count ? 'text-yellow-400' : 'text-red-400'}`} />
              <div>
                <div className={dynastyTheme.classes.text.white}>
                  {selectionStats.count} player{selectionStats.count !== 1 ? 's' : ''} selected
                </div>
                {isCommissionerMode && (
                  <div className="text-xs text-yellow-400">Will be added to {activeTeamName}</div>
                )}
              </div>
              {rosterCapacityAnalysis && (
                <div className="flex items-center gap-2">
                  {rosterCapacityAnalysis.availableSlots < selectionStats.count ? (
                    <div className="flex items-center gap-1 text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs">Only {rosterCapacityAnalysis.availableSlots} slots available</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-green-400">
                      <Shield className="w-4 h-4" />
                      <span className="text-xs">{rosterCapacityAnalysis.availableSlots} slots available</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={clearAllSelections} className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}>
                Clear All
              </button>
              <button
                onClick={handleIntelligentBatchAdd}
                disabled={loading || !rosterCapacityAnalysis}
                className={`${rosterCapacityAnalysis?.availableSlots >= selectionStats.count ? dynastyTheme.utils.getComponent('button', 'primary', 'md') : 'px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors'} disabled:opacity-50 flex items-center gap-2`}
              >
                <UserPlus className="w-4 h-4" />
                {rosterCapacityAnalysis?.availableSlots >= selectionStats.count ? 'Assign Positions' : 'Try Add Anyway'} ({selectionStats.count})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FILTER BAR — single compact row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">

        {/* View mode pill toggle */}
        <div className="flex rounded border border-gray-700 overflow-hidden flex-shrink-0">
          <button
            onClick={() => handleViewModeChange('free_agents')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'free_agents' ? 'bg-yellow-400 text-black' : 'text-gray-400 hover:text-white'}`}
          >
            FA Only{viewMode === 'free_agents' && totalCount > 0 ? ` · ${totalCount}` : ''}
          </button>
          <button
            onClick={() => handleViewModeChange('all_players')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-700 ${viewMode === 'all_players' ? 'bg-yellow-400 text-black' : 'text-gray-400 hover:text-white'}`}
          >
            All Players
          </button>
        </div>

        <div className="w-px h-5 bg-gray-700 flex-shrink-0" />

        {/* Batters / Pitchers pill toggle */}
        <div className="flex rounded border border-gray-700 overflow-hidden flex-shrink-0">
          <button
            onClick={() => handleTabChange('hitting')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeTab === 'hitting' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
          >
            Batters
          </button>
          <button
            onClick={() => handleTabChange('pitching')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-700 ${activeTab === 'pitching' ? 'bg-yellow-400/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
          >
            Pitchers
          </button>
        </div>

        <div className="w-px h-5 bg-gray-700 flex-shrink-0" />

        {/* Search form */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'hitting' ? 'hitters' : 'pitchers'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${dynastyTheme.components.input} pl-7 h-7 w-36 text-xs`}
            />
          </div>
          <select
            value={positionFilter}
            onChange={(e) => { setPositionFilter(e.target.value); clearAllSelections(); }}
            className={`${dynastyTheme.components.input} h-7 text-xs w-20 appearance-none cursor-pointer`}
          >
            {(activeTab === 'hitting' ? hitterPositions : pitcherPositions).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={loading}
            className="h-7 px-3 bg-yellow-400 text-black rounded text-xs font-medium hover:bg-yellow-500 disabled:opacity-50 transition-colors"
          >
            Search
          </button>
          {(searchTerm || positionFilter !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setPositionFilter('all');
                clearAllSelections();
                clearAdvancedFilters();
                if (typeof loadPlayers === 'function') loadPlayers(0, false);
              }}
              className="h-7 px-2 text-xs text-gray-400 hover:text-white transition-colors flex items-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </form>

        {/* Right side controls — pushed to end */}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1 h-7 px-2.5 text-xs rounded border transition-colors ${showAdvancedFilters ? 'border-yellow-400/50 text-yellow-400 bg-yellow-400/10' : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'}`}
          >
            <Filter className="w-3 h-3" />
            Filters
            {showAdvancedFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {/* Recent — rolling-row visibility toggle. */}
          <button
            onClick={() => setShowRecent(!showRecent)}
            aria-pressed={showRecent}
            title={showRecent ? 'Hide the rolling stats row' : 'Show the rolling stats row'}
            className={`flex items-center gap-1 h-7 px-2.5 text-xs rounded border transition-colors ${showRecent ? 'border-yellow-400/50 text-yellow-400 bg-yellow-400/10' : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'}`}
          >
            {showRecent ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
            Recent {showRecent ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* ADVANCED FILTERS PANEL */}
      {showAdvancedFilters && (
        <div className={`${dynastyTheme.components.card.base} p-4 mb-4`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date Range Filter */}
            <div className="md:col-span-4">
              <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                <Calendar className="w-3 h-3 inline mr-1" />
                Date Range
              </label>
              <div className="flex gap-2">
                {['season', 'last7', 'last14', 'last30', 'custom'].map(range => (
                  <button
                    key={range}
                    onClick={() => setAdvancedFilters(prev => ({ ...prev, dateRange: range }))}
                    className={`px-3 py-1 text-xs rounded ${advancedFilters.dateRange === range ? 'bg-yellow-400 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'} transition-colors`}
                  >
                    {range === 'season' ? 'Full Season' : range === 'last7' ? 'Last 7 Days' : range === 'last14' ? 'Last 14 Days' : range === 'last30' ? 'Last 30 Days' : 'Custom'}
                  </button>
                ))}
              </div>
              {advancedFilters.dateRange === 'custom' && (() => {
                // 9o.6.e Slice 2 Commit 8-rest: inline validation feedback.
                // Skip presence errors on initial empty state (don't shout at the user
                // before they've typed). Format/semantic errors surface as soon as
                // input has any value.
                const validation = validateCustomWindow(
                  advancedFilters.customStartDate,
                  advancedFilters.customEndDate,
                );
                const bothEmpty =
                  !advancedFilters.customStartDate && !advancedFilters.customEndDate;
                const showStartError =
                  !bothEmpty && validation.errors.start;
                const showEndError =
                  !bothEmpty && validation.errors.end;
                return (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col">
                        <input
                          type="date"
                          value={advancedFilters.customStartDate}
                          onChange={(e) => setAdvancedFilters(prev => ({ ...prev, customStartDate: e.target.value }))}
                          aria-invalid={!!showStartError}
                          aria-label="Custom window start date"
                          className={`${dynastyTheme.components.input} text-xs ${showStartError ? dynastyTheme.classes.border.error : ''}`}
                        />
                        {showStartError && (
                          <span
                            role="alert"
                            data-testid="fa-custom-start-error"
                            className={`${dynastyTheme.classes.text.error} text-xs mt-1`}
                          >
                            {validation.errors.start}
                          </span>
                        )}
                      </div>
                      <span className={`${dynastyTheme.classes.text.neutralLight} pt-2`}>to</span>
                      <div className="flex flex-col">
                        <input
                          type="date"
                          value={advancedFilters.customEndDate}
                          onChange={(e) => setAdvancedFilters(prev => ({ ...prev, customEndDate: e.target.value }))}
                          aria-invalid={!!showEndError}
                          aria-label="Custom window end date"
                          className={`${dynastyTheme.components.input} text-xs ${showEndError ? dynastyTheme.classes.border.error : ''}`}
                        />
                        {showEndError && (
                          <span
                            role="alert"
                            data-testid="fa-custom-end-error"
                            className={`${dynastyTheme.classes.text.error} text-xs mt-1`}
                          >
                            {validation.errors.end}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Hitter Filters */}
            {activeTab === 'hitting' && (
              <>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>At Bats (AB)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minAB} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minAB: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxAB} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxAB: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Home Runs (HR)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minHR} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minHR: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxHR} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxHR: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Games (G)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minG} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minG: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxG} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxG: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label
                    className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1 ${qualificationDisabled ? 'opacity-50' : ''}`}
                  >
                    MLB Qualified
                  </label>
                  <div
                    className="flex items-center gap-2"
                    title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={qualificationDisabled ? false : advancedFilters.hitterQualified}
                      disabled={qualificationDisabled}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, hitterQualified: e.target.checked }))}
                      className={`w-4 h-4 rounded border-gray-600 ${qualificationDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <span className={`text-xs text-gray-400 ${qualificationDisabled ? 'opacity-50' : ''}`}>≥{advancedFilters.qualifiedPA} PA</span>
                    <input
                      type="number"
                      value={advancedFilters.qualifiedPA}
                      disabled={qualificationDisabled}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, qualifiedPA: e.target.value }))}
                      className={`${dynastyTheme.components.input} text-xs w-16 ml-1 ${qualificationDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={qualificationDisabled ? qualificationDisabledTooltip : 'Set custom PA threshold'}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Pitcher Filters */}
            {activeTab === 'pitching' && (
              <>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Strikeouts (K)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minK} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minK: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxK} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxK: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Games Started (GS)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minGS} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minGS: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxGS} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxGS: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Innings Pitched (IP)</label>
                  <div className="flex gap-1">
                    <input type="number" placeholder="Min" value={advancedFilters.minIP} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minIP: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                    <input type="number" placeholder="Max" value={advancedFilters.maxIP} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxIP: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                  </div>
                </div>
                <div>
                  <label
                    className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1 ${qualificationDisabled ? 'opacity-50' : ''}`}
                  >
                    MLB Qualified
                  </label>
                  <div
                    className="flex items-center gap-2"
                    title={qualificationDisabled ? qualificationDisabledTooltip : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={qualificationDisabled ? false : advancedFilters.pitcherQualified}
                      disabled={qualificationDisabled}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, pitcherQualified: e.target.checked }))}
                      className={`w-4 h-4 rounded border-gray-600 ${qualificationDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <span className={`text-xs text-gray-400 ${qualificationDisabled ? 'opacity-50' : ''}`}>≥{advancedFilters.qualifiedIP} IP</span>
                    <input
                      type="number"
                      value={advancedFilters.qualifiedIP}
                      disabled={qualificationDisabled}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, qualifiedIP: e.target.value }))}
                      className={`${dynastyTheme.components.input} text-xs w-16 ml-1 ${qualificationDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={qualificationDisabled ? qualificationDisabledTooltip : 'Set custom IP threshold'}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Price Filter (both tabs) */}
            <div>
              <label className={`block text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>Price ($)</label>
              <div className="flex gap-1">
                <input type="number" placeholder="Min" value={advancedFilters.minPrice} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minPrice: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
                <input type="number" placeholder="Max" value={advancedFilters.maxPrice} onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxPrice: e.target.value }))} className={`${dynastyTheme.components.input} text-xs w-20`} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { clearAdvancedFilters(); if (typeof loadPlayers === 'function') loadPlayers(0, false); }}
              className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
            >
              <X className="w-3 h-3 mr-1" />
              Clear Filters
            </button>
            <button onClick={applyAdvancedFilters} className={`${dynastyTheme.utils.getComponent('button', 'primary', 'sm')} flex items-center gap-1`}>
              <TrendingUp className="w-3 h-3" />
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* RESULTS ROW */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-400">
          {loading ? (
            'Loading...'
          ) : totalCount > 0 ? (
            <>
              {players.length} {activeTab === 'hitting' ? 'hitters' : 'pitchers'}
              <span className="text-gray-600 mx-1">·</span>
              {showAll ? 'all players' : 'free agents'}
              {positionFilter !== 'all' && <span> · {positionFilter === 'MI' ? 'Middle Infield' : positionFilter === 'CI' ? 'Corner Infield' : positionFilter}</span>}
              {searchTerm && <span> matching "<span className="text-yellow-400">{searchTerm}</span>"</span>}
              {bulkMode && selectionStats.hasSelections && <span className="ml-1 text-yellow-400">· {selectionStats.count} selected</span>}
            </>
          ) : (
            <>
              No {activeTab === 'hitting' ? 'hitters' : 'pitchers'} found
              {positionFilter !== 'all' && <span> · {positionFilter === 'MI' ? 'Middle Infield' : positionFilter === 'CI' ? 'Corner Infield' : positionFilter}</span>}
              {searchTerm && <span> matching "<span className="text-yellow-400">{searchTerm}</span>"</span>}
            </>
          )}
        </p>
        {renderCapacityIndicator()}
      </div>
    </>
  );
};

export default SearchControls;